import React, { useEffect, useState } from 'react';
import {
  Card,
  Image,
  Text,
  ActionIcon,
  Menu,
  Tooltip,
  Badge,
  Modal,
  ScrollArea,
  Group,
  Divider,
} from '@mantine/core';
import {
  IconDots,
  IconTrash,
  IconExternalLink,
  IconArchive,
  IconArchiveOff,
  IconSend,
  IconRestore,
  IconFile,
  IconFileSymlink,
  IconUsers,
} from '@tabler/icons-react';
import file from '../../assets/file.png';
import { useNavigate } from 'react-router-dom';
import CollaboratorModal from "../CollaboratorModal";

export default function FormCard({
  formName,
  formDescription,
  id,
  onDelete,
  onArchive,
  offArchive,
  handleSend,
  Template,
  handleRestore,
  restoreTemplate,
  Trash,
  buitInTemplate,
  TemplateForm,
  isDraft,
  viewTemplate,
  onAction,
  accessRole = 'OWNER', // default for My Forms
  shared = false,
}) {
  const navigate = useNavigate();
  const [encryptedId, setEncryptedId] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [collabOpen, setCollabOpen] = useState(false);

  const isLongDescription = formDescription && formDescription.length > 80;
  const truncatedDescription = isLongDescription
    ? formDescription.substring(0, 60) + '...'
    : formDescription;

    const canEdit = accessRole === 'OWNER' || accessRole === 'EDITOR';
    const canDelete = accessRole === 'OWNER';
    const canArchive = accessRole === 'OWNER';
    const canSend = accessRole === 'OWNER';

  // --- Token + Encryption Logic ---
  function getTokenFromCookie() {
    const cookies = document.cookie.split(';');
    const tokenCookie = cookies.find((cookie) => cookie.trim().startsWith('token='));
    return tokenCookie ? tokenCookie.split('=')[1] : null;
  }

  useEffect(() => {
    async function encryptId() {
      try {
        const token = getTokenFromCookie();
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/encryptId`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
          body: JSON.stringify({ id }),
        });

        if (!response.ok) throw new Error('Error encrypting id');
        const data = await response.json();
        setEncryptedId(data.encryptedId);
      } catch (error) {
        console.error('Error encrypting ID:', error);
      }
    }

    encryptId();
  }, [id, formName]);

  // --- Navigation ---
  function handleExternalLink() {
    if (!encryptedId) return;
    if (viewTemplate || Template)
      navigate(`/buildTemplate?formName=${encodeURIComponent(formName)}`);
    else if (TemplateForm)
      navigate(`/build?templateName=${encodeURIComponent(formName)}&useTemplate=true`);
    else {
      navigate(`/viewForm?id=${encryptedId}`);
      if (onAction) onAction(id);
    }
  }

  function handleResponses() {
    if (!encryptedId) return;
    navigate(`/Responses?formId=${encryptedId}`);
    if (onAction) onAction(id);
  }
  
  async function handleOpenForm() {
  try {
    const token = getTokenFromCookie();
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/create-form-from-template`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        templateId: id,
        formName,
        email: localStorage.getItem("email")
      }),
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || "Error creating form from template");

    const newFormId = result.form.id;

    const encryptedIdResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/encryptId`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id: newFormId }),
      }
    );


    const { encryptedId } = await encryptedIdResponse.json();
    navigate(`/viewForm?id=${encryptedId}`);

  } catch (error) {
    console.error("Error opening template form:", error);
  }
}



  // --- Tooltip Texts ---
  const deleteTooltip = Template ? 'Delete Template' : 'Delete Form';
  const viewTooltip = Template || viewTemplate ? 'View Template' : 'View Form';
  const archiveTooltip = Template ? 'Archive Template' : 'Archive Form';
  const unarchiveTooltip = Template ? 'Unarchive Template' : 'Unarchive Form';
  const restoreTooltip = Template ? 'Restore Template' : 'Restore Form';
  const sendTooltip = 'Send Form';
  const responsesTooltip = 'Responses';
  const createForm = 'Use Template';

  return (
    <>
      <Card
        shadow="md"
        radius="lg"
        p="lg"
        m="sm"
        w={300}
        h={260}
        style={{
          position: 'relative',
          background:
            'linear-gradient(145deg, rgba(255,255,255,1) 0%, rgba(240,248,245,1) 100%)',
          border: '1px solid #e2e8f0',
          transition: 'all 0.25s ease',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.05)';
        }}
      >
        {Template && (
          <Badge
            color="teal"
            variant="filled"
            style={{
              position: 'absolute',
              top: 10,
              left: 10,
              fontSize: '0.7rem',
            }}
          >
            TEMPLATE
          </Badge>
        )}

        {/* Action Menu */}
        <Menu shadow="md" width={200} position="bottom-end">
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                color: '#555',
              }}
            >
              <IconDots size={21} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {!isDraft && Template && !Trash && (
              <Menu.Item
                leftSection={<IconFileSymlink size={14} color="#2596be" />}
                onClick={() => handleOpenForm(id)}
              >
                {createForm}
              </Menu.Item>
            )}
            <Menu.Item
              leftSection={<IconExternalLink size={14} color="#2596be" />}
              onClick={handleExternalLink}
            >
              {viewTooltip}
            </Menu.Item>
            {!Template && !isDraft && (
              <Menu.Item
                leftSection={<IconFile size={14} color="#2596be" />}
                onClick={handleResponses}
              >
                {responsesTooltip}
              </Menu.Item>
            )}
            {accessRole === 'OWNER' && !Template && !Trash && !isDraft && (
              <Menu.Item
                leftSection={<IconUsers size={14} color="#2596be" />}
                onClick={() => setCollabOpen(true)}
              >
                Collaborators
              </Menu.Item>
            )}
            {Trash && !Template &&(
              <Menu.Item
                leftSection={<IconRestore size={14} color="#2596be" />}
                onClick={() => handleRestore(id)}
              >
                {restoreTooltip}
              </Menu.Item>
            )}
            {Trash && Template &&(
              <Menu.Item
                leftSection={<IconRestore size={14} color="#2596be" />}
                onClick={() => restoreTemplate(id)}
              >
                {restoreTooltip}
              </Menu.Item>
            )}
            {!isDraft && !Template && !Trash && canSend && (
              <Menu.Item
                leftSection={<IconSend size={14} color="#2596be" />}
                onClick={() => handleSend(id)}
              >
                {sendTooltip}
              </Menu.Item>
            )}
            {!buitInTemplate && !Trash && canArchive && (
              <Menu.Item
                leftSection={
                  offArchive ? (
                    <IconArchiveOff size={14} color="#2596be" />
                  ) : (
                    <IconArchive size={14} color="#2596be" />
                  )
                }
                onClick={offArchive ? offArchive : onArchive}
              >
                {offArchive ? unarchiveTooltip : archiveTooltip}
              </Menu.Item>
            )}
            {!buitInTemplate && canDelete && (
              <Menu.Item
                leftSection={<IconTrash size={14} color="red" />}
                onClick={onDelete}
              >
                {deleteTooltip}
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>

        <Group position="center" mt="sm">
          <Image
            src={file}
            w={60}
            h={60}
            alt="Form Icon"
            radius="md"
            style={{ marginTop: '10px' }}
          />
        </Group>

        {shared && (
          <Badge
            color={accessRole === "EDITOR" ? "#4f85f1ff" : "#4db0e5ff"}
            style={{ position: "absolute", bottom: 10, right: 10 }}
          >
            {accessRole}
          </Badge>
        )}

        {isDraft && (
          <Badge
            color="teal"
            ml="sm"
            variant="filled"
            position="center"
            style={{
              position: 'absolute',
              top: 80,
              right: 10,
              fontSize: '0.7rem',
            }}
          >
            Draft
          </Badge>
        )}

        <Text
          fw={600}
          size="lg"
          ta="center"
          mt="md"
          style={{
            color: '#1e293b',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textTransform: 'capitalize',
          }}
        >
          {formName}
        </Text>

        <Divider my="sm" />

        <Text
          size="sm"
          c="dimmed"
          ta="center"
          style={{
            height: '40px',
            overflow: 'hidden',
            lineHeight: 1.4,
            padding: '0 6px',
          }}
        >
          {truncatedDescription}
          {isLongDescription && (
            <Text
              component="span"
              size="xs"
              c="#159c82"
              style={{
                cursor: 'pointer',
                marginLeft: '4px',
                textDecoration: 'underline',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowFullDescription(true);
              }}
            >
              Show more
            </Text>
          )}
        </Text>
      </Card>
      <CollaboratorModal
        opened={collabOpen}
        onClose={() => setCollabOpen(false)}
        formId={id}
        formName={formName}
      />

      <Modal
        opened={showFullDescription}
        onClose={() => setShowFullDescription(false)}
        title={formName}
        size="lg"
        centered
        overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}
      >
          <Divider size={'sm'}/>
        <ScrollArea style={{ maxHeight: '300px' }}>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {formDescription}
          </Text>
        </ScrollArea>
      </Modal>
    </>
  );
}
